import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAppSelector } from '@redux/hooks';
import { merchantPaymentAccountService } from '@services/merchantPaymentAccount';
import { useTranslation } from '@hooks/useTranslation';

export const MY_PAYMENT_ACCOUNT_QUERY_KEY = 'myPaymentAccount';

const errorMessage = (error: any, fallback: string): string => {
  return error?.response?.errors?.[0]?.message || error?.message || fallback;
};

export const useMyPaymentAccount = () => {
  const { organizationId } = useAppSelector((state) => state.organization);
  return useQuery({
    queryKey: [MY_PAYMENT_ACCOUNT_QUERY_KEY, organizationId],
    queryFn: () => merchantPaymentAccountService.getMyPaymentAccount(),
    enabled: !!organizationId,
  });
};

export const useConnectNmiAccount = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  const { organizationId } = useAppSelector((state) => state.organization);
  return useMutation({
    mutationFn: ({ securityKey }: { securityKey: string }) =>
      merchantPaymentAccountService.connectNmiAccount(securityKey),
    onSuccess: () => {
      toast.success(tActionMessages.paymentAccountConnected);
      queryClient.invalidateQueries({
        queryKey: [MY_PAYMENT_ACCOUNT_QUERY_KEY, organizationId],
        refetchType: 'active',
      });
    },
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Failed to connect payment account'));
    },
  });
};

export const useDisconnectNmiAccount = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  const { organizationId } = useAppSelector((state) => state.organization);
  return useMutation({
    mutationFn: () => merchantPaymentAccountService.disconnectNmiAccount(),
    onSuccess: () => {
      toast.success(tActionMessages.paymentAccountRemoved);
      queryClient.invalidateQueries({
        queryKey: [MY_PAYMENT_ACCOUNT_QUERY_KEY, organizationId],
        refetchType: 'active',
      });
    },
    onError: (error: any) => {
      toast.error(errorMessage(error, 'Failed to remove payment account'));
    },
  });
};
